'use client'

import { Container, Flex } from '@quark/layout'
import { DriveHeroProps, ProductModal } from '@interfaces/home'
import React, { useState } from 'react'
import { Text } from '@quark/typography'
import { Layer } from '@quark/layer'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { Animations, MultiReveal, Reveal } from '@quark/animation'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import DriveCard from './DriveCard'
import styles from './DriveHero.module.scss'
import DriveModal from './DriveModal'

const DriveHero = (data: DriveHeroProps) => {
  const [show, setShow] = useState(false)
  const [modalData, setModalData] = useState<ProductModal | null>(null)
  const handleCardClick = async (
    eventProperties: {
      eventName?: string
      customProperties?: Record<string, any>
    },
    mobileUrl: string,
    url: string,
    urlInNewTab: boolean,
    hasModal: boolean,
    modalData: ProductModal
  ) => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 757px)').matches && hasModal) {
      setShow(true)
      setModalData(modalData)
      await trackEvent(eventProperties?.eventName ?? 'track_event_complete', {
        experiment_name: 'wrapper_v3',
        page_type: 'acko_home_page',
        ...eventProperties?.customProperties,
      })
      return
    }
    await trackEvent(eventProperties?.eventName ?? 'track_event_complete', {
      experiment_name: 'wrapper_v3',
      page_type: 'acko_home_page',
      redirection_url: data?.isMobile ? mobileUrl : url,
      ...eventProperties?.customProperties,
    })
    window.open(data?.isMobile ? mobileUrl : url, urlInNewTab ? '_blank' : '_self')
  }
  return (
    <Container id={NewHomePageComponent.DriveHero} type="fluid">
      <PropertyControlledComponent controllerProperty={!!modalData} fallback={null}>
        <DriveModal show={show} setShow={setShow} modalData={modalData ?? undefined} />
      </PropertyControlledComponent>
      <div className={styles.driveHeroContainer}>
        <Layer
          type="image"
          imgLg="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Light_theme_Desktop_8569ea68bc.webp"
          imgMd="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Light_theme_Tablet_541dfa937d.webp"
          imgSm="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Light_theme_Mobile_d066947f10.webp"
        // imgDarkLg="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Dark_theme_Desktop_dee4a33410.webp"
        // imgDarkMd="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Dark_theme_Tablet_5511b4ad3f.webp"
        // imgDarkSm="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Drive_Section_BG_Dark_theme_Mobile_117756337b.webp"
        >
          <Container
            type="root"
            px={{
              lg: 'spacing-80',
              md: 'spacing-32',
              sm: 'spacing-16',
            }}
            py={{
              lg: 'spacing-72',
              md: 'spacing-56',
              sm: 'spacing-32',
            }}>
            <Flex
              flexDirection="column"
              justifyContent="space-between"
              gap={{ lg: 'spacing-56', md: 'spacing-40', sm: 'spacing-32' }}>
              <MultiReveal
                as={Flex}
                key="driveSection-headings"
                flexDirection="column"
                alignItems="center"
                gap={{
                  lg: 'spacing-16',
                  md: 'spacing-16',
                  sm: 'spacing-8',
                }}
                animationDelays={(i: number) => i * 250}
                animationProps={{ animation: Animations.FadeIn }}>
                <span key="driveSection-title">
                  <Text
                    as="p"
                    fontStyle={{ lg: 'um_text-4xl/bold', md: 'um_text-3xl/bold', sm: 'um_text-2xl/bold' }}
                    color="base/primary">
                    {data?.title}
                  </Text>
                </span>
                <div key="driveSection-poweredBy">
                  <Flex gap={{ lg: 'spacing-8', md: 'spacing-8', sm: 'spacing-4' }} alignItems="center">
                    <Text
                      fontStyle={{ lg: 'um_text-sm/normal', md: 'um_text-sm/normal', sm: 'um_text-xs/normal' }}
                      color="base/primary">
                      Powered by
                    </Text>
                    <picture className={styles.adLogo}>
                      <source
                        srcSet="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/ACKO_Drive_wide_0df6c0a2c4.webp"
                        media="screen and (prefers-color-scheme: dark)"
                      />
                      <img
                        src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/ACKO_Drive_wide_0df6c0a2c4.webp"
                        alt="AckoDrive Wide Logo"
                        width={132}
                        height={22}
                      />
                    </picture>
                  </Flex>
                </div>
              </MultiReveal>

              <Flex
                flexDirection={{ lg: 'row', md: 'row', sm: 'column' }}
                gap={{ lg: 'spacing-16', md: 'spacing-12', sm: 'spacing-12' }}>
                {data?.driveProducts?.map((product, i) => (
                  <div key={i} className={styles.driveCardContainer}>
                    <Reveal
                      as="div"
                      className={styles.revealCard}
                      animationProps={{
                        threshold: 0.1,
                        animationDelayMs: (i + 1) * 100,
                      }}>
                      <DriveCard product={product} onClick={handleCardClick} />
                    </Reveal>
                  </div>
                ))}
              </Flex>
            </Flex>
          </Container>
        </Layer>
        <div className={`${styles.rechargeButton} ${styles.button1}`}>
          {/* https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Copy_duplicate_document_file_multiply_cf810f2341.webp */}
          <Flex gap="spacing-4" flexDirection={{ lg: 'row', md: 'row', sm: 'column' }} alignItems="center">
            <picture className={styles.rechargeButtonIcon}>
              <source
                srcSet="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Copy_duplicate_document_file_multiply_cf810f2341.webp"
                media="screen and (prefers-color-scheme: dark)"
              />
              <img
                src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Copy_duplicate_document_file_multiply_cf810f2341.webp"
                alt="AckoDrive Wide Logo"
                width={24}
                height={24}
              />
            </picture>
            <Text
              fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-xs/medium', sm: 'um_text-2xs/normal' }}
              color="base/primary"
              textAlign="center">
              Save car documents
            </Text>
          </Flex>
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="27" viewBox="0 0 43 27" fill="none">
            <path
              d="M2.5 0C1.11929 0 0 1.11929 0 2.5C0 3.88071 1.11929 5 2.5 5C3.70949 5 4.71857 4.14114 4.9502 3H14.6426C23.0383 3 30.7963 7.47911 34.9941 14.75L41.9229 26.75L42.7881 26.25L35.8604 14.25C31.4838 6.66967 23.3956 2 14.6426 2H4.9502C4.71857 0.85886 3.70949 0 2.5 0Z"
              fill="url(#paint0_linear_10263_18136)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_10263_18136"
                x1="-2.17239"
                y1="0.55729"
                x2="44.5821"
                y2="26.3111"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="white" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className={`${styles.rechargeButton} ${styles.button2}`}>
          {/* https://acko-cms-dev.s3.ap-south-1.amazonaws.com/border_boundary_limit_toll_fastag_737722ee1a.webp */}
          <Flex gap="spacing-4" flexDirection={{ lg: 'row', md: 'row', sm: 'column' }} alignItems="center">
            <picture className={styles.rechargeButtonIcon}>
              <source
                srcSet="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/border_boundary_limit_toll_fastag_737722ee1a.webp"
                media="screen and (prefers-color-scheme: dark)"
              />
              <img
                src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/border_boundary_limit_toll_fastag_737722ee1a.webp"
                alt="AckoDrive Wide Logo"
                width={24}
                height={24}
              />
            </picture>
            <Text
              fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-xs/medium', sm: 'um_text-2xs/normal' }}
              color="base/primary"
              textAlign="center">
              Recharge FASTag
            </Text>
          </Flex>
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="27" viewBox="0 0 43 27" fill="none">
            <path
              d="M2.5 0C1.11929 0 0 1.11929 0 2.5C0 3.88071 1.11929 5 2.5 5C3.70949 5 4.71857 4.14114 4.9502 3H14.6426C23.0383 3 30.7963 7.47911 34.9941 14.75L41.9229 26.75L42.7881 26.25L35.8604 14.25C31.4838 6.66967 23.3956 2 14.6426 2H4.9502C4.71857 0.85886 3.70949 0 2.5 0Z"
              fill="url(#paint0_linear_10263_18136)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_10263_18136"
                x1="-2.17239"
                y1="0.55729"
                x2="44.5821"
                y2="26.3111"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="white" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className={`${styles.rechargeButton} ${styles.button3}`}>
          {/* https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Traffic_fine_challan_ec33e74532.webp */}
          <Flex gap="spacing-4" flexDirection={{ lg: 'row', md: 'row', sm: 'column' }} alignItems="center">
            <picture className={styles.rechargeButtonIcon}>
              <source
                srcSet="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Traffic_fine_challan_ec33e74532.webp"
                media="screen and (prefers-color-scheme: dark)"
              />
              <img
                src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Traffic_fine_challan_ec33e74532.webp"
                alt="AckoDrive Wide Logo"
                width={24}
                height={24}
              />
            </picture>
            <Text
              fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-xs/medium', sm: 'um_text-2xs/normal' }}
              color="base/primary"
              textAlign="center">
              Check traffic fines
            </Text>
          </Flex>
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="27" viewBox="0 0 43 27" fill="none">
            <path
              d="M40.2891 0C41.6698 0 42.7891 1.11929 42.7891 2.5C42.7891 3.88071 41.6698 5 40.2891 5C39.0796 5 38.0705 4.14114 37.8389 3H28.1465C19.7508 3 11.9928 7.47911 7.79492 14.75L0.866211 26.75L0.000976562 26.25L6.92871 14.25C11.3052 6.66967 19.3935 2 28.1465 2H37.8389C38.0705 0.85886 39.0796 0 40.2891 0Z"
              fill="url(#paint0_linear_10263_18129)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_10263_18129"
                x1="44.9615"
                y1="0.55729"
                x2="-1.79308"
                y2="26.3111"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="white" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className={`${styles.rechargeButton} ${styles.button4}`}>
          {/* https://acko-cms-dev.s3.ap-south-1.amazonaws.com/PUCC_d6406298e8.webp */}
          <Flex gap="spacing-4" flexDirection={{ lg: 'row', md: 'row', sm: 'column' }} alignItems="center">
            <picture className={styles.rechargeButtonIcon}>
              <source
                srcSet="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/PUCC_d6406298e8.webp"
                media="screen and (prefers-color-scheme: dark)"
              />
              <img
                src="https://acko-cms-dev.s3.ap-south-1.amazonaws.com/PUCC_d6406298e8.webp"
                alt="AckoDrive Wide Logo"
                width={24}
                height={24}
              />
            </picture>
            <Text
              fontStyle={{ lg: 'um_text-sm/medium', md: 'um_text-xs/medium', sm: 'um_text-2xs/normal' }}
              color="base/primary"
              textAlign="center">
              Get PUCC reminders
            </Text>
          </Flex>
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="27" viewBox="0 0 43 27" fill="none">
            <path
              d="M40.2891 0C41.6698 0 42.7891 1.11929 42.7891 2.5C42.7891 3.88071 41.6698 5 40.2891 5C39.0796 5 38.0705 4.14114 37.8389 3H28.1465C19.7508 3 11.9928 7.47911 7.79492 14.75L0.866211 26.75L0.000976562 26.25L6.92871 14.25C11.3052 6.66967 19.3935 2 28.1465 2H37.8389C38.0705 0.85886 39.0796 0 40.2891 0Z"
              fill="url(#paint0_linear_10263_18129)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_10263_18129"
                x1="44.9615"
                y1="0.55729"
                x2="-1.79308"
                y2="26.3111"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="white" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </Container>
  )
}

export default DriveHero
